import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  DEFAULT_CURRENCY,
  getExchangeRates,
  getSavedCurrency,
  saveCurrencyPreference,
  convertCurrency,
  formatCurrency,
  convertAndFormatPrice,
  getCurrencySymbol,
  getCurrencyOptions,
} from '../utils/currency';

// Create Currency Context
const CurrencyContext = createContext(null);

/**
 * Currency Provider Component
 * Manages global currency state and provides conversion utilities
 */
export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState(DEFAULT_CURRENCY);
  const [exchangeRates, setExchangeRates] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Initialize currency and exchange rates on mount
   */
  useEffect(() => {
    const initializeCurrency = async () => {
      try {
        setIsLoading(true);
        
        // Load saved currency preference
        const savedCurrency = getSavedCurrency();
        setSelectedCurrency(savedCurrency);
        
        // Load exchange rates
        const rates = await getExchangeRates();
        setExchangeRates(rates);
        
        setError(null);
      } catch (err) {
        console.error('Error initializing currency:', err);
        setError('Failed to load exchange rates');
      } finally {
        setIsLoading(false);
      }
    };

    initializeCurrency();
  }, []);

  /**
   * Set up automatic rate refresh every 12 hours
   */
  useEffect(() => {
    const refreshInterval = setInterval(async () => {
      try {
        const rates = await getExchangeRates();
        setExchangeRates(rates);
      } catch (err) {
        console.error('Error refreshing exchange rates:', err);
      }
    }, 12 * 60 * 60 * 1000); // 12 hours

    return () => clearInterval(refreshInterval);
  }, []);

  /**
   * Change selected currency
   */
  const changeCurrency = useCallback((currencyCode) => {
    setSelectedCurrency(currencyCode);
    saveCurrencyPreference(currencyCode);
  }, []);

  /**
   * Convert price from USD to selected currency
   */
  const convertPrice = useCallback((amountInUSD) => {
    return convertCurrency(amountInUSD, selectedCurrency, exchangeRates);
  }, [selectedCurrency, exchangeRates]);

  /**
   * Format amount in selected currency
   */
  const formatPrice = useCallback((amount) => {
    return formatCurrency(amount, selectedCurrency);
  }, [selectedCurrency]);

  /**
   * Convert and format price in one go
   */
  const convertAndFormat = useCallback((amountInUSD) => {
    return convertAndFormatPrice(amountInUSD, selectedCurrency, exchangeRates);
  }, [selectedCurrency, exchangeRates]);

  /**
   * Get current currency symbol
   */
  const currencySymbol = useMemo(() => {
    return getCurrencySymbol(selectedCurrency);
  }, [selectedCurrency]);

  /**
   * Get all available currencies
   */
  const availableCurrencies = useMemo(() => {
    return getCurrencyOptions();
  }, []);

  /**
   * Refresh exchange rates manually
   */
  const refreshRates = useCallback(async () => {
    try {
      setIsLoading(true);
      const rates = await getExchangeRates();
      setExchangeRates(rates);
      setError(null);
    } catch (err) {
      console.error('Error refreshing rates:', err);
      setError('Failed to refresh exchange rates');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Context value
  const value = {
    // State
    selectedCurrency,
    exchangeRates,
    isLoading,
    error,
    currencySymbol,
    availableCurrencies,
    
    // Methods
    changeCurrency,
    convertPrice,
    formatPrice,
    convertAndFormat,
    refreshRates,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

/**
 * Custom hook to use Currency Context
 */
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  
  return context;
};

export default CurrencyContext;
